import React, { useState } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Tabs,
  Select,
  InputNumber,
  message,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import {
  AddAppFeature_api,
  AddAppFeatureCategory_api,
  FetchAppFeatureCategories_api,
} from "../../../Api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

interface FeatureManagementModalProps {
  open: boolean;
  onCancel: () => void;
}

const { TabPane } = Tabs;

export const FeatureManagementModal: React.FC<FeatureManagementModalProps> = ({
  open,
  onCancel,
}) => {
  const [formCategory] = Form.useForm();
  const [formFeature] = Form.useForm();
  const queryClient = useQueryClient();

  const { data: categoriesData, refetch: refetchCategories } = useQuery({
    queryKey: ["appFeatureCategories"],
    queryFn: FetchAppFeatureCategories_api,
  });

  const addCategoryMutation = useMutation({
    mutationFn: AddAppFeatureCategory_api,
    onSuccess: (res) => {
      if (res?.success) {
        message.success(res.msg);
        formCategory.resetFields();
        refetchCategories();
      }
    },
  });

  const addFeatureMutation = useMutation({
    mutationFn: AddAppFeature_api,
    onSuccess: (res) => {
      if (res?.success) {
        message.success(res.msg);
        formFeature.resetFields();
        queryClient.invalidateQueries({ queryKey: ["appFeatures"] });
      }
    },
  });

  const handleAddCategory = (values: any) => {
    addCategoryMutation.mutate(values);
  };

  const handleAddFeature = (values: any) => {
    addFeatureMutation.mutate(values);
  };

  return (
    <Modal
      title="Manage App Features & Categories"
      open={open}
      onCancel={onCancel}
      footer={null}
      width={600}
    >
      <Tabs defaultActiveKey="1">
        <TabPane tab="Add Category" key="1">
          <Form
            form={formCategory}
            layout="vertical"
            onFinish={handleAddCategory}
            initialValues={{ order: 0, icon: "PieChart" }}
          >
            <Form.Item
              name="name"
              label="Category Name"
              rules={[{ required: true, message: "Please enter category name" }]}
            >
              <Input placeholder="e.g., Reports, Sales, Settings" />
            </Form.Item>
            <Form.Item name="order" label="Display Order">
              <InputNumber min={0} className="w-full" />
            </Form.Item>
            <Form.Item name="icon" label="Icon Name (Lucide)">
              <Input placeholder="e.g., PieChart, Users, Settings" />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={addCategoryMutation.isPending}
                icon={<PlusOutlined />}
                block
              >
                Add Category
              </Button>
            </Form.Item>
          </Form>
        </TabPane>
        <TabPane tab="Add Feature" key="2">
          <Form form={formFeature} layout="vertical" onFinish={handleAddFeature}>
            <Form.Item
              name="app_feature_category_id"
              label="Category"
              rules={[{ required: true, message: "Please select a category" }]}
            >
              <Select placeholder="Select a category">
                {categoriesData?.categories?.map((cat: any) => (
                  <Select.Option key={cat.id} value={cat.id}>
                    {cat.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="feature"
              label="Feature Name"
              rules={[{ required: true, message: "Please enter feature name" }]}
            >
              <Input placeholder="e.g., Manage Users, Sales Report" />
            </Form.Item>
            <Form.Item
              name="navigation"
              label="Navigation Path"
              rules={[{ required: true, message: "Please enter navigation path" }]}
            >
              <Input placeholder="e.g., manage_users, report_by_date" />
            </Form.Item>
            <Form.Item name="icon" label="Icon Name (Lucide)" initialValue="PieChart">
              <Input placeholder="e.g., PieChart, Users, Settings" />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={addFeatureMutation.isPending}
                icon={<PlusOutlined />}
                block
              >
                Add Feature
              </Button>
            </Form.Item>
          </Form>
        </TabPane>
      </Tabs>
    </Modal>
  );
};
